/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useRef } from "react";
import { BEM, useToggle } from "@adapptio/lib-components";
import { FieldWrapper } from "../FieldWrapper";
import { ISchemaStringWithConstConstItem } from "@adapptio/lib-blueprint";
import { IFieldValidationError } from "../FieldTypes";
import {
	ClickAwayListener,
	ColorPicker,
	ColorPickerTabs,
	ColorPickerTile,
	FC_ANCHOR,
	FC_DIRECTION,
	FC_SIZE,
	IColorPickerProps,
	Icon,
	ICON_SIZE,
	InputAdornment,
	InputButton
} from "../../core";
import { FieldInput } from "../FieldInput";
import {
	FIELD_FLOATING_PANEL_SCHEMA,
	FIELD_FLOATING_PANEL_STYLE,
	FieldFloatingPanel
} from "../FieldFloatingPanel";

import "./FieldColor.scss";
import { FieldStatus } from "../FieldStatus";
import { isFunction } from "@adapptio/lib-shared";

export interface IFieldColorProps {
	id?: string;
	label?: string;
	value: string;
	constants?: ISchemaStringWithConstConstItem[];
	placeholder?: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	onChange?: (value: string) => void;
	grow?: boolean;
	clearable?: boolean;
}

const FieldColor: React.FC<IFieldColorProps> = ({
	id,
	label,
	value,
	constants,
	placeholder,
	readOnly,
	validationErrors,
	className,
	onChange,
	grow,
	clearable
}) => {
	const fieldRef = useRef();
	const [ pickerOpen, togglePicker, openPicker, closePicker ] = useToggle();
	const baseClass = BEM("edt-field-color", null, {
		"picker-open": pickerOpen
	});

	const handleChange = useCallback<IColorPickerProps["onChange"]>(
		(value) => {
			if (onChange) {
				onChange(value);
			}
		},
		[ onChange ]
	);

	const handleChangeInput = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
		(event) => {
			const val = event.target.value;

			onChange(val);
		},
		[ onChange ]
	);

	const handleOpenPickerClick = useCallback<React.MouseEventHandler<HTMLElement>>(
		(event) => {
			if (readOnly) {
				return;
			}

			openPicker();
		},
		[ openPicker, readOnly ]
	);

	const handleTogglePickerClick = useCallback<React.MouseEventHandler<HTMLElement>>(
		(event) => {
			if (readOnly) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			togglePicker();
		},
		[ togglePicker, readOnly ]
	);

	const handleClearClick = useCallback(() => {
		if (isFunction(onChange)) {
			onChange(null);
		}
	}, [ onChange ]);

	return (
		<ClickAwayListener onClickAway={closePicker}>
			<FieldWrapper
				type="color"
				className={className}
				grow={grow}
				ref={fieldRef}
				onClick={handleOpenPickerClick}
				isFocused
			>
				<ColorPicker
					palette={constants}
					value={value || ""}
					onChange={handleChange}
					onClose={closePicker}
				>
					<FieldInput
						id={id}
						className={baseClass.toClassName()}
						type="text"
						value={value ?? ""}
						placeholder={placeholder}
						readOnly={readOnly}
						onChange={handleChangeInput}
						startAdornment={
							<InputAdornment square center>
								<ColorPickerTile className={baseClass.element("preview").toClassName()} />
							</InputAdornment>
						}
						endAdornment={
							<InputAdornment>
								{validationErrors?.length > 0 && <FieldStatus errors={validationErrors} />}
								{clearable && (
									<InputButton
										className={baseClass.element("editor-clear").toClassName()}
										onClick={handleClearClick}
										tabIndex={-1}
										disabled={!value}
									>
										<Icon
											className={baseClass.element("editor-clear-icon").toClassName()}
											name="mdi/close"
										/>
									</InputButton>
								)}
								<InputButton
									className={baseClass.element("picker-trigger").toClassName()}
									onClick={handleTogglePickerClick}
									tabIndex={-1}
									disabled={readOnly}
								>
									<Icon
										className={baseClass.element("picker-trigger-icon").toClassName()}
										name="mdi/menu-down"
										size={ICON_SIZE.XSMALL}
									/>
								</InputButton>
							</InputAdornment>
						}
					/>
					<FieldFloatingPanel
						title={label}
						for={fieldRef}
						isOpened={pickerOpen}
						anchor={FC_ANCHOR.CENTER_BOTTOM}
						direction={FC_DIRECTION.HR}
						size={FC_SIZE.FIT_MIN}
						style={FIELD_FLOATING_PANEL_STYLE.STICKY}
						schema={FIELD_FLOATING_PANEL_SCHEMA.PRIMARY}
						onClose={closePicker}
						disableBackdrop
					>
						<ColorPickerTabs />
					</FieldFloatingPanel>
				</ColorPicker>
			</FieldWrapper>
		</ClickAwayListener>
	);
};

const FieldColorMemo = React.memo(FieldColor);

export { FieldColorMemo as FieldColor };
